import {Box, Card, CardContent, Chip, Container, Grid2, Stack, Typography} from "@mui/material";
import {
  FileFragmentFragment,
  FileType,
  RentGuaranteeType,
  RentType,
  useCreateZyusetuMutation,
  ZyusetuFieldSectionFragmentFragment,
  ZyusetuType,
} from "@/graphql/graphql.ts";
import {useCallback, useEffect, useMemo, useState} from "react";
import {PdfViewer} from "@/components/PdfViewer.tsx";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {useGetFirstContent} from "@/hooks/useGetFirstContent.ts";
import {useNavigate, useParams} from "react-router-dom";
import {FileUploader} from "@/components/pages/zyusetu-new/FileUploader.tsx";
import {AnalyzeResult} from "@/components/pages/zyusetu-new/AnalyzeResult.tsx";
import {ZyusetuCreationForm} from "@/components/pages/zyusetu-new/ZyusetuCreationForm.tsx";
import {FILE_TYPE_ICONS, FileTypeConfig, FileTypeLabel} from "@/types/FileType.tsx";
import ZumenSample from "@/assets/images/zumenSample.png";
import reinsDetailText from "@/assets/images/reinsDetailText.png";
import {MultiSelectorCard} from "@/components/input/MultiSelectorCard.tsx";

// 利用可能なファイルタイプの定義
const AVAILABLE_FILE_TYPES: FileTypeConfig[] = [
  {
    type: FileType.Zumen,
    icon: FILE_TYPE_ICONS[FileType.Zumen],
    label: FileTypeLabel[FileType.Zumen],
    sampleImage: ZumenSample,
    sampleTitle: "募集図面の例",
    zyusetuType: ZyusetuType.Rent,
    isAvailable: true,
  },
  {
    type: FileType.ReinsText,
    icon: FILE_TYPE_ICONS[FileType.ReinsText],
    label: FileTypeLabel[FileType.ReinsText],
    sampleImage: reinsDetailText,
    sampleTitle: "Reins掲載情報\nAtHomeBBなど、\n物件詳細ページでCTRL+Aで全選択してコピーして、当ページに貼り付けてください\n※現在ベータ版",
    zyusetuType: ZyusetuType.Rent,
    isAvailable: true,
  },
];

export function RentNewZyusetuPage() {
  const {zyusetuType} = useParams<{ zyusetuType: ZyusetuType }>();
  const [selectedFileType, setSelectedFileType] = useState<FileTypeConfig | null>(null);
  const [zumen, setZumen] = useState<FileFragmentFragment | null>();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [zyusetuTitle, setZyusetuTitle] = useState<string | null>(null);
  const [analyzeResult, setAnalyzeResult] = useState<ZyusetuFieldSectionFragmentFragment[] | null>();
  
  const getFirstContent = useGetFirstContent();
  
  const getBuildingTitle = useCallback((result: ZyusetuFieldSectionFragmentFragment[]) => {
    const section = result?.find((section) => section.title === "物件情報");
    if (!section) return "";
    const name = section.keys.find((key) => key.keyName === "buildingName");
    const room = section.keys.find((key) => key.keyName === "roomNumber");
    const roomNumber = getFirstContent(room?.analyzedPayloads);
    return (
      `${getFirstContent(name?.analyzedPayloads) ?? ""}` +
      (roomNumber ? `${roomNumber}号室` : "")
    );
  }, [getFirstContent]);
  
  const nav = useNavigate();
  const createZyusetu = useCreateZyusetuMutation();
  
  const handleCreateZyusetu = useCallback(async (zumen: FileFragmentFragment, templateId: string, title: string) => {
    if (title.includes("/")) {
      toast.error("ファイル名に/を含めることはできません")
    } else {
      const zyusetu = await createZyusetu.mutateAsync({
        templateId: templateId,
        fileIds: [zumen.id],
        title,
      });
      if (!zyusetu.createZyusetu) {
        toast.error("Failed to create zyusetu");
        return;
      }
      nav(`/zyusetu/${zyusetu.createZyusetu.id}/edit?ref=new`);
    }
  }, [nav, createZyusetu]);
  
  const rentType = useMemo(() => {
    const section = analyzeResult?.find((section) => section.title === "契約種別");
    if (!section) return undefined;
    const name = section.keys.find((key) => key.keyName === "contract_type");
    if (!name) return undefined
    switch (getFirstContent(name.analyzedPayloads)) {
      case "普通賃貸借":
        return RentType.NormalRent;
      case "定期賃貸借":
        return RentType.FixedTermRent;
    }
  }, [analyzeResult, getFirstContent]);
  
  const rentGuaranteeType = useMemo(() => {
    const section = analyzeResult?.find((section) => section.title === "保証会社");
    if (!section) return undefined;
    const name = section.keys.find((key) => key.keyName === "guaranteeCompany_required");
    if (!name) return undefined
    switch (getFirstContent(name.analyzedPayloads)) {
      case true:
        return RentGuaranteeType.GuaranteeCompany;
      default:
        return RentGuaranteeType.Guarantor;
    }
  }, [analyzeResult, getFirstContent]);
  
  useEffect(() => {
    if (!zyusetuType) {
      nav("/")
      return;
    }
    if (!Object.keys(ZyusetuType).includes(zyusetuType)) {
      nav("/404")
      return;
    }
  }, [nav, zyusetuType]);
  
  if (!zyusetuType) {
    nav("/")
    return null;
  }
  
  const handleAnalyzeComplete = (result: ZyusetuFieldSectionFragmentFragment[], file: FileFragmentFragment) => {
    setAnalyzeResult(result);
    setZumen(file);
    const today = dayjs().format("YYYY年MM月DD日");
    setZyusetuTitle(`${today} - ${getBuildingTitle(result)}`);
  };
  
  return (
    <>
      {!analyzeResult ? (
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 4, mt: 2}}>
            <Typography variant="pageTitle">
              STEP1: アップロードするファイルを選択してください
            </Typography>
            <Chip label={"賃貸"} color={'secondary'} sx={{fontWeight: "bold"}}/>
          </Stack>
          <MultiSelectorCard
            onSelect={setSelectedFileType}
            options={AVAILABLE_FILE_TYPES}
            selected={selectedFileType}
          />
          {selectedFileType && (
            <Box sx={{mt: 4}}>
              <FileUploader
                fileType={selectedFileType}
                onAnalyzeComplete={handleAnalyzeComplete}
                zyusetuType={zyusetuType}
              />
              {selectedFileType.sampleImage && (
                <Card sx={{mt: 4}}>
                  <CardContent>
                    <Typography fontWeight={"bold"} mb={2} whiteSpace={'pre-wrap'}>
                      {selectedFileType.sampleTitle}
                    </Typography>
                    <img
                      src={selectedFileType.sampleImage}
                      style={{height: "350px", width: "600px", objectFit: "contain"}}
                      alt={selectedFileType.sampleTitle}
                    />
                  </CardContent>
                </Card>
              )}
            
            </Box>
          )}
        </Container>
      ) : (
        <Container
          maxWidth={"xl"}
          sx={{
            width: "100%",
            height: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 4, mt: 2}}>
            <Typography variant="pageTitle">
              STEP2: 重説情報を確認して作成してください
            </Typography>
          </Stack>
          <Card sx={{
            bgcolor: "background.paper",
            p: 3
          }}>
            <Grid2
              container
              spacing={2}
              height={"100%"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Grid2 size={5}>
                <AnalyzeResult sections={analyzeResult} file={zumen!}/>
                <ZyusetuCreationForm
                  title={zyusetuTitle || ""}
                  onTitleChange={setZyusetuTitle}
                  rentType={rentType}
                  rentGuaranteeType={rentGuaranteeType}
                  zyusetuType={zyusetuType}
                  onSubmit={() => handleCreateZyusetu(zumen!, templateId!, zyusetuTitle!)}
                  disabled={!zumen || !templateId || !zyusetuTitle}
                  onTemplateSelect={setTemplateId}
                />
              </Grid2>
              <Grid2 size={7}>
                <Card>
                  <CardContent>
                    <PdfViewer/>
                  </CardContent>
                </Card>
              </Grid2>
            </Grid2>
          </Card>
        </Container>
      )}
    </>
  );
}
